// @flow

import { Global, css } from '@emotion/react'
import styled from '@emotion/styled'
import * as React from 'react'
import Helmet from 'react-helmet'

import { colors } from '@toggl/style'

import { CookieWarning } from '../CookieWarning'

declare var process: WebProcess

export const LayoutRoot = ({
  title,
  description,
  children,
  ogImage,
  ogImageAlt,
  favicon,
  noSmoothScrolling = false,
}: {
  title?: string,
  description?: string,
  children: React.Node,
  ogImage?: string,
  ogImageAlt?: string,
  favicon?: string,
  noSmoothScrolling?: boolean,
}): React.Element<typeof LayoutRoot> => (
  <StyledLayoutRoot>
    <Helmet
      htmlAttributes={{
        lang: 'en',
      }}
    >
      <title itemProp="name">{title}</title>
      <meta property="og:title" content={title} />
      <meta
        name="description"
        property="og:description"
        content={
          description ||
          'When time tracking is this easy, you can get more done. Toggl Track data brings clarity around profitability and helps you make better business decisions.'
        }
      />
      <meta
        property="og:image"
        content={ogImage || require('assets/toggl-track-main.jpg')}
      />
      {ogImageAlt && <meta property="og:image:alt" content={ogImageAlt} />}
      <meta name="twitter:card" content="summary_large_image" />
      <link
        rel="icon"
        type="image/png"
        href={favicon || require('assets/favicon-track.png')}
        sizes="32x32"
      />
      <link
        rel="preconnect"
        href={new URL(process.env.GATSBY_PUBLIC_PATH).origin}
      />
      <link
        rel="preconnect"
        href={new URL(process.env.GATSBY_PUBLIC_PATH).origin}
        crossOrigin="anonymous"
      />
      <link
        rel="preload"
        href={require('@toggl/fonts/gt-haptik-medium-500.latin.woff2')}
        as="font"
        type="font/woff2"
        crossOrigin="anonymous"
      />
      <link
        rel="preload"
        href={require('@toggl/fonts/gt-haptik-medium-rotalic-500.latin.woff2')}
        as="font"
        type="font/woff2"
        crossOrigin="anonymous"
      />
      <link
        rel="preload"
        href={require('@toggl/fonts/inter-400.latin.woff2')}
        as="font"
        type="font/woff2"
        crossOrigin="anonymous"
      />
      <link
        rel="preload"
        href={require('@toggl/fonts/inter-500.latin.woff2')}
        as="font"
        type="font/woff2"
        crossOrigin="anonymous"
      />
      <link rel="dns-prefetch" href="https://www.googletagmanager.com" />
      <link rel="dns-prefetch" href="https://www.googleadservices.com" />
      <link rel="dns-prefetch" href="https://googleads.g.doubleclick.net" />
      <link rel="dns-prefetch" href="https://stats.g.doubleclick.net" />
      <link rel="dns-prefetch" href="https://bat.bing.com" />
      <link rel="dns-prefetch" href="https://facebook.com" />
      <link rel="dns-prefetch" href="https://connect.facebook.net" />
      <link rel="dns-prefetch" href="https://px.ads.linkedin.com" />
      <link rel="dns-prefetch" href="https://snap.licdn.com" />
    </Helmet>

    {noSmoothScrolling && (
      <Global
        styles={css`
          html {
            scroll-behavior: auto;
          }
        `}
      />
    )}
    {children}
    <CookieWarning />
  </StyledLayoutRoot>
)

const StyledLayoutRoot = styled.div`
  background-color: ${colors.darkPurple};
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  position: relative;
`
